<template>
  <div class="daily-summary">
    <header class="summary-header">
      <div>
        <p class="eyebrow">Contrôle du jour</p>
        <h1>Résumé journée</h1>
        <p>Suivi mobile des retards, sorties tôt et absences.</p>
      </div>
      <button type="button" class="icon-btn refresh-btn" @click="loadSummary" :disabled="loading" aria-label="Actualiser">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ spin: loading }">
          <path d="M21 12a9 9 0 1 1-2.64-6.36" />
          <path d="M21 3v6h-6" />
        </svg>
      </button>
    </header>

    <section class="filters-card">
      <label class="field">
        Journée
        <input v-model="selectedDate" type="date" @change="loadSummary" />
      </label>
      <label class="field">
        Groupe
        <select v-model="selectedGroup" @change="loadSummary">
          <option value="">Tous les groupes</option>
          <option v-for="group in workGroups" :key="group.group_name" :value="group.group_name">
            {{ group.group_name }}
          </option>
        </select>
      </label>
      <label class="field search-field">
        Recherche
        <div class="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input v-model.trim="searchQuery" type="search" placeholder="Nom employé..." />
        </div>
      </label>
    </section>

    <section v-if="summary" class="day-strip">
      <div>
        <span>Journée</span>
        <strong>{{ formatDate(summary.date) }}</strong>
      </div>
      <div>
        <span>Clôture</span>
        <strong>{{ summary.rh_closing_time }}</strong>
      </div>
    </section>

    <div v-if="loading" class="state-card">Chargement...</div>
    <div v-else-if="errorMessage" class="state-card error">{{ errorMessage }}</div>
    <div v-else-if="filteredStaff.length === 0" class="state-card">Aucun employé à afficher.</div>

    <template v-else>
      <section class="count-row">
        <div><span>Total</span><strong>{{ counts.total || 0 }}</strong></div>
        <div class="ok"><span>OK</span><strong>{{ counts.respected || 0 }}</strong></div>
        <div class="warn"><span>Attention</span><strong>{{ warningCount }}</strong></div>
        <div class="bad"><span>Problème</span><strong>{{ badCount }}</strong></div>
      </section>

      <section class="employee-list">
        <article
          v-for="staff in filteredStaff"
          :key="staff.id"
          class="employee-card"
          :class="staff.respect_severity"
          tabindex="0"
          role="button"
          @click="openStaffSheet(staff)"
          @keydown.enter.prevent="openStaffSheet(staff)"
          @keydown.space.prevent="openStaffSheet(staff)"
        >
          <div class="employee-photo">
            <img v-if="staff.pic" :src="staffImage(staff)" :alt="staff.first_name" />
            <strong v-else>{{ firstLetter(staff) }}</strong>
          </div>

          <div class="employee-main">
            <div class="employee-top">
              <h2>{{ staff.first_name }} {{ staff.last_name }}</h2>
              <span class="status-pill" :class="staff.respect_severity">{{ staff.status_label }}</span>
            </div>

            <div class="group-line">{{ staff.group_name || 'Sans groupe' }}</div>

            <div class="time-line">
              <div>
                <span>Prévu</span>
                <strong>{{ formatClock(staff.scheduled_start) }} - {{ formatClock(staff.scheduled_end) }}</strong>
              </div>
              <div>
                <span>Réel</span>
                <strong>{{ realLine(staff) }}</strong>
              </div>
            </div>
          </div>
        </article>
      </section>
    </template>

    <transition name="fade">
      <div v-if="selectedStaff" class="sheet-backdrop" @click.self="closeStaffSheet">
        <section class="staff-sheet" role="dialog" aria-modal="true" aria-label="Détail employé">
          <button class="sheet-close" type="button" @click="closeStaffSheet" aria-label="Fermer">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>

          <div class="sheet-identity">
            <div class="sheet-photo">
              <img v-if="selectedStaff.pic" :src="staffImage(selectedStaff)" :alt="selectedStaff.first_name" />
              <strong v-else>{{ firstLetter(selectedStaff) }}</strong>
            </div>
            <div>
              <h2>{{ selectedStaff.first_name }} {{ selectedStaff.last_name }}</h2>
              <span class="status-pill" :class="selectedStaff.respect_severity">{{ selectedStaff.status_label }}</span>
            </div>
          </div>

          <div class="sheet-meta">
            <div>
              <span>Groupe</span>
              <strong>{{ selectedStaff.group_name || 'Sans groupe' }}</strong>
            </div>
            <div>
              <span>Durée</span>
              <strong>{{ selectedStaff.duration || '-' }}</strong>
            </div>
          </div>

          <div class="sheet-times">
            <div>
              <span>Prévu</span>
              <strong>{{ formatClock(selectedStaff.scheduled_start) }} - {{ formatClock(selectedStaff.scheduled_end) }}</strong>
            </div>
            <div>
              <span>Réel</span>
              <strong>{{ realLine(selectedStaff) }}</strong>
            </div>
          </div>

          <a
            v-if="whatsappLink(selectedStaff)"
            class="whatsapp-action"
            :href="whatsappLink(selectedStaff)"
            target="_blank"
            rel="noopener"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12.04 2C6.55 2 2.08 6.33 2.08 11.65c0 1.7.47 3.36 1.35 4.82L2 22l5.72-1.45a10.2 10.2 0 0 0 4.32.96c5.49 0 9.96-4.33 9.96-9.65S17.53 2 12.04 2Zm0 17.82c-1.36 0-2.69-.35-3.86-1.02l-.28-.16-3.37.86.84-3.21-.18-.29a8 8 0 0 1-1.42-4.35c0-4.39 3.71-7.96 8.27-7.96s8.27 3.57 8.27 7.96-3.71 8.17-8.27 8.17Zm4.55-5.96c-.25-.12-1.48-.71-1.71-.79-.23-.08-.4-.12-.57.12-.17.24-.65.79-.8.95-.15.16-.3.18-.55.06-.25-.12-1.06-.38-2.01-1.2-.74-.64-1.24-1.43-1.39-1.67-.15-.24-.02-.37.11-.49.12-.11.25-.29.38-.43.13-.14.17-.24.25-.4.08-.16.04-.3-.02-.43-.06-.12-.57-1.33-.78-1.82-.21-.47-.42-.41-.57-.42h-.49c-.17 0-.44.06-.67.3-.23.24-.88.83-.88 2.03 0 1.2.9 2.36 1.02 2.52.13.16 1.77 2.62 4.29 3.67.6.25 1.07.4 1.43.51.6.19 1.15.16 1.58.1.48-.07 1.48-.59 1.69-1.16.21-.57.21-1.06.15-1.16-.06-.1-.23-.16-.48-.28Z" />
            </svg>
            <span>WhatsApp</span>
          </a>

          <div v-else class="sheet-note">
            WhatsApp disponible pour retard, sortie tôt, sortie manquante ou absence avec un téléphone valide.
          </div>
        </section>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'AppDailySummary',
  data() {
    return {
      summary: null,
      staffList: [],
      counts: {},
      workGroups: [],
      selectedDate: '',
      selectedGroup: '',
      searchQuery: '',
      loading: false,
      errorMessage: '',
      selectedStaff: null,
    };
  },
  computed: {
    filteredStaff() {
      if (!this.searchQuery) return this.staffList;
      const query = this.searchQuery.toLowerCase();
      return this.staffList.filter((staff) => `${staff.first_name || ''} ${staff.last_name || ''}`.toLowerCase().includes(query));
    },
    warningCount() {
      return (this.counts.late || 0) + (this.counts.late_working || 0) + (this.counts.missing_exit || 0);
    },
    badCount() {
      return (this.counts.not_pointed || 0) + (this.counts.absent || 0) + (this.counts.early_exit || 0) + (this.counts.coverage_gap || 0);
    },
  },
  methods: {
    async fetchGroups() {
      try {
        const response = await fetch('/api-pointage/fetchgroups', { credentials: 'include', cache: 'no-store' });
        const data = response.ok ? await response.json() : [];
        this.workGroups = Array.isArray(data) ? data : [];
      } catch {
        this.workGroups = [];
      }
    },
    async loadSummary() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const params = new URLSearchParams();
        if (this.selectedDate) params.set('date', this.selectedDate);
        if (this.selectedGroup) params.set('group', this.selectedGroup);
        const url = `/api-pointage/daily-summary${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await fetch(url, { credentials: 'include', cache: 'no-store' });
        if (!response.ok) throw new Error('Erreur chargement résumé');
        const data = await response.json();
        this.summary = data;
        this.staffList = Array.isArray(data.staff) ? data.staff : [];
        this.counts = data.counts || {};
        if (!this.selectedDate && data.date) this.selectedDate = data.date;
      } catch (error) {
        this.errorMessage = error.message || 'Erreur chargement résumé';
        this.staffList = [];
        this.counts = {};
      } finally {
        this.loading = false;
      }
    },
    openStaffSheet(staff) {
      this.selectedStaff = staff;
    },
    closeStaffSheet() {
      this.selectedStaff = null;
    },
    firstLetter(staff) {
      return (staff.first_name || '?').charAt(0).toUpperCase();
    },
    staffImage(staff) {
      if (!staff.pic) return '';
      return staff.pic.startsWith('/api-pointage/uploads/') ? staff.pic : `/api-pointage/uploads/${staff.pic}`;
    },
    formatDate(value) {
      if (!value) return '-';
      const [year, month, day] = String(value).slice(0, 10).split('-');
      return year && month && day ? `${day}/${month}/${year}` : value;
    },
    formatClock(value) {
      if (!value) return '-';
      const parts = String(value).replace('T', ' ').split(' ');
      return parts[1] ? parts[1].slice(0, 5) : value;
    },
    realLine(staff) {
      if (!staff.actual_in) return '-';
      const out = staff.actual_out ? this.formatClock(staff.actual_out) : '...';
      return `${this.formatClock(staff.actual_in)} - ${out}`;
    },
    durationLabel(minutes) {
      const value = Math.max(0, Math.abs(Number(minutes || 0)));
      const h = Math.floor(value / 60);
      const m = value % 60;
      if (h && m) return `${h}h ${m}min`;
      if (h) return `${h}h`;
      return `${m}min`;
    },
    cleanWhatsappPhone(phone) {
      let value = String(phone || '').trim();
      if (!value) return '';
      value = value.replace(/[^\d+]/g, '');
      if (value.startsWith('00')) value = value.slice(2);
      if (value.startsWith('+')) value = value.slice(1);
      if (/^0[67]\d{8}$/.test(value)) return `212${value.slice(1)}`;
      if (/^212[67]\d{8}$/.test(value)) return value;
      return value.length >= 10 ? value : '';
    },
    whatsappMessage(staff) {
      if (!staff) return '';
      if (staff.status === 'late' || staff.status === 'late_working') {
        return `Salam, jiti retard lyom b ${this.durationLabel(staff.entry_diff_minutes)}.`;
      }
      if (staff.status === 'early_exit') {
        return `Salam, khrjti bekri lyom b ${this.durationLabel(staff.exit_diff_minutes)}.`;
      }
      if (staff.status === 'missing_exit') {
        return 'Salam, nssiti tpointi sortie dyalk lyom.';
      }
      if (staff.status === 'absent') {
        return 'Salam, majitich lkhdma lyom.';
      }
      return '';
    },
    whatsappLink(staff) {
      const phone = this.cleanWhatsappPhone(staff?.phone);
      const message = this.whatsappMessage(staff);
      if (!phone || !message) return '';
      return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    },
  },
  async mounted() {
    await this.fetchGroups();
    await this.loadSummary();
  },
};
</script>

<style scoped>
.daily-summary {
  max-width: 680px;
  margin: 0 auto;
  padding: 10px 0 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.eyebrow {
  margin: 0 0 3px;
  color: #2563eb;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.summary-header h1 {
  margin: 0;
  color: #0f172a;
  font-size: 25px;
  font-weight: 900;
}

.summary-header p:last-child {
  margin: 3px 0 0;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
}

.icon-btn {
  width: 44px;
  height: 44px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #eff6ff;
  color: #1d4ed8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.icon-btn svg {
  width: 19px;
  height: 19px;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.filters-card,
.day-strip,
.count-row,
.state-card {
  background: white;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.05);
}

.filters-card {
  padding: 12px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.search-field {
  grid-column: 1 / -1;
}

.field {
  display: grid;
  gap: 5px;
  color: #334155;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.field input,
.field select,
.search-box {
  width: 100%;
  height: 42px;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  background: white;
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
  padding: 0 11px;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-box svg {
  width: 17px;
  height: 17px;
  color: #64748b;
}

.search-box input {
  border: 0;
  outline: 0;
  padding: 0;
  height: 100%;
  flex: 1;
  min-width: 0;
  text-transform: none;
  font-weight: 700;
}

.day-strip,
.count-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.day-strip div,
.count-row div {
  padding: 11px 12px;
  display: grid;
  gap: 3px;
  border-right: 1px solid #e2e8f0;
}

.day-strip div:last-child,
.count-row div:last-child {
  border-right: 0;
}

.count-row {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.day-strip span,
.count-row span {
  color: #64748b;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
}

.day-strip strong,
.count-row strong {
  color: #0f172a;
  font-size: 15px;
  font-weight: 900;
}

.count-row .ok strong { color: #15803d; }
.count-row .warn strong { color: #c2410c; }
.count-row .bad strong { color: #b91c1c; }

.state-card {
  padding: 18px;
  text-align: center;
  color: #64748b;
  font-weight: 800;
}

.state-card.error {
  color: #b91c1c;
  background: #fef2f2;
  border-color: #fecaca;
}

.employee-list {
  display: grid;
  gap: 10px;
}

.employee-card {
  background: white;
  border: 1px solid #dbe4f0;
  border-left: 4px solid #94a3b8;
  border-radius: 8px;
  padding: 11px;
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  gap: 11px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
  cursor: pointer;
  text-align: left;
}

.employee-card:focus-visible {
  outline: 3px solid rgba(37, 99, 235, 0.22);
  outline-offset: 2px;
}

.employee-card.ok { border-left-color: #15803d; }
.employee-card.warn { border-left-color: #ea580c; }
.employee-card.bad { border-left-color: #dc2626; }
.employee-card.neutral { border-left-color: #94a3b8; }

.employee-photo,
.sheet-photo {
  border-radius: 8px;
  border: 1px solid #dbe4f0;
  background: #f1f5f9;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-weight: 900;
}

.employee-photo {
  width: 64px;
  height: 64px;
  font-size: 24px;
}

.employee-photo img,
.sheet-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.employee-main {
  min-width: 0;
}

.employee-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.employee-top h2 {
  margin: 0;
  color: #0f172a;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  border-radius: 999px;
  padding: 4px 9px;
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
}

.status-pill.ok {
  background: #ecfdf5;
  color: #15803d;
}

.status-pill.warn {
  background: #fff7ed;
  color: #c2410c;
}

.status-pill.bad {
  background: #fef2f2;
  color: #b91c1c;
}

.status-pill.neutral {
  background: #f1f5f9;
  color: #64748b;
}

.group-line {
  margin-top: 7px;
  color: #475569;
  font-size: 13px;
  font-weight: 800;
}

.time-line {
  margin-top: 9px;
  display: grid;
  gap: 5px;
}

.time-line div,
.sheet-times div,
.sheet-meta div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.time-line span,
.sheet-times span,
.sheet-meta span {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.time-line strong,
.sheet-times strong,
.sheet-meta strong {
  color: #0f172a;
  font-size: 13px;
  font-weight: 900;
  text-align: right;
}

.sheet-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(15, 23, 42, 0.42);
  padding: 16px;
}

.staff-sheet {
  width: min(100%, 480px);
  max-height: min(82vh, 620px);
  overflow: auto;
  border-radius: 14px 14px 8px 8px;
  background: white;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
  padding: 16px;
  display: grid;
  gap: 14px;
  position: relative;
}

.sheet-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sheet-close svg {
  width: 18px;
  height: 18px;
}

.sheet-identity {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding-right: 42px;
}

.sheet-photo {
  width: 64px;
  height: 64px;
  font-size: 24px;
}

.sheet-identity h2 {
  margin: 0 0 6px;
  color: #0f172a;
  font-size: 19px;
  font-weight: 900;
  line-height: 1.2;
}

.sheet-meta,
.sheet-times {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  display: grid;
  gap: 9px;
  background: #f8fafc;
}

.whatsapp-action {
  min-height: 48px;
  border-radius: 8px;
  background: #16a34a;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  font-size: 15px;
  font-weight: 900;
  text-decoration: none;
}

.whatsapp-action svg {
  width: 22px;
  height: 22px;
}

.sheet-note {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  padding: 12px;
  font-size: 13px;
  font-weight: 800;
  text-align: center;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 480px) {
  .daily-summary {
    padding: 0 0 20px;
  }

  .summary-header {
    align-items: flex-start;
  }

  .summary-header h1 {
    font-size: 22px;
  }

  .filters-card {
    grid-template-columns: 1fr;
  }

  .search-field {
    grid-column: auto;
  }

  .count-row span {
    font-size: 9px;
  }

  .count-row div {
    padding: 10px 8px;
  }

  .employee-card {
    grid-template-columns: 58px minmax(0, 1fr);
    gap: 10px;
    padding: 10px;
  }

  .employee-photo {
    width: 58px;
    height: 58px;
  }

  .employee-top {
    display: grid;
    gap: 6px;
  }

  .status-pill {
    width: fit-content;
  }

  .sheet-backdrop {
    padding: 10px;
  }
}
</style>
